<template>
    <div>
        <v-btn
            color="primary"
            :block="block"
            :large="large"
            v-show="isVisible"
            @click.prevent="typeof handler === 'function' ? handler(buyProduct) : buyProduct()"
            :disabled="disabled"
            :round="round"
            :depressed="depressed"
            class="basket-button font-weight-bold my-0 px-0"
            :class="{ 'px-2': $vuetify.breakpoint.xsOnly }"
        >
            <v-icon size="18" class="mr-1">shopping_cart</v-icon>

            <template v-if="isBuyable">
                {{ buttonText ? buttonText : 'in the cart' | translation }}
            </template>
            <template v-else>
                {{ 'Select a variant' | translation }}
            </template>
        </v-btn>

        <out-of-stock-modal />

        <limit-per-order-modal />

        <in-cart-modal />

        <out-of-stock-dialog v-model="quotaExceededModal" :buyable-amount="buyableAmount" />
    </div>
</template>

<style>
.basket-button {
    border-radius: 0 !important;
}
</style>

<script>
import OutOfStockModal from '../product-page/OutOfStockModal';
import LimitPerOrderModal from '../product-page/LimitPerOrderModal';
import InCartModal from '../product-page/InCartModal';
import OutOfStockDialog from './OutOfStockDialog';

import { createNamespacedHelpers } from 'vuex';

const parentModuleName = 'Product';
const { mapGetters, mapState, mapActions, mapMutations } = createNamespacedHelpers(parentModuleName);

export default {
    name: 'BasketButton',
    components: {
        OutOfStockModal,
        LimitPerOrderModal,
        InCartModal,
        OutOfStockDialog,
    },
    props: {
        product: {},
        currentMerchantId: {},
        amountValue: {},
        currentProductId: {},
        buttonText: String,
        large: {
            type: Boolean,
            default: true,
        },
        handler: {},
        buyOptions: {},
        disabled: Boolean,
        round: Boolean,
        depressed: Boolean,
        block: {
            type: Boolean,
            default: true,
        },
        requireBuyOptions: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            quotaExceededModal: false,
        };
    },
    computed: {
        isBuyable() {
            return this.isProductBuyable(this.product, this.currentMerchantId, this.currentProductId);
        },
        isVisible() {
            return !this.currentProductId || this.isBuyable;
        },
        ...mapGetters(['merchantQuota', 'isSellOutOfStock', 'isProductBuyable', 'getBasketItem']),
        ...mapGetters({
            getAmountValueState: 'getAmountValue',
            getExistingBasketItem: 'getExistingBasketItem',
        }),
        getAmountValue() {
            return this.amountValue || this.getAmountValueState;
        },
        buyableAmount() {
            return this.merchantQuota(this.product, this.currentProductId, this.currentMerchantId);
        },
    },
    methods: {
        ...mapActions({
            dispatchBuyProduct: 'buyProduct',
        }),
        ...mapMutations(['setShowOutOfStockModal', 'setDisplayBuyOptionsModal', 'setShowLimitPerOrderModal']),
        buyProduct() {
            if (!this.isBuyable) {
                this.$emit('selectVariant');
            } else {
                const amountValue = this.getAmountValue;

                const itemConfig = {
                    product: this.product,
                    currentProductId: this.currentProductId,
                    currentMerchantId: this.currentMerchantId,
                    quantity: amountValue,
                    buyOptions: this.buyOptions ? this.buyOptions : {},
                    keepProduct: true,
                };
                const phantomBasketItem = this.getBasketItem(itemConfig);
                const productType = this.product.type;
                let limitPerOrder = 0;
                if (!productType || productType === 'product') {
                    limitPerOrder = this.$basket.getLimitPerOrder(
                        amountValue,
                        false,
                        this.product,
                        phantomBasketItem.catalog_item_id,
                        phantomBasketItem.options.merchant,
                    );
                }

                const existingBasketItem = this.getExistingBasketItem(itemConfig);

                const merchantQuota = this.buyableAmount;

                let totalAmountValue = amountValue;
                if (existingBasketItem && existingBasketItem.quantity) {
                    totalAmountValue += existingBasketItem.quantity;
                }

                if (limitPerOrder > 0) {
                    this.setShowLimitPerOrderModal(true);
                } else if (merchantQuota === null || totalAmountValue <= merchantQuota || merchantQuota <= 0) {
                    if (
                        this.product &&
                        this.product.buyOptions &&
                        this.product.buyOptions.length &&
                        this.requireBuyOptions
                    ) {
                        this.setDisplayBuyOptionsModal(true);

                        return Promise.resolve();
                    }

                    this.$emit('beforeAddToBasket', phantomBasketItem);

                    return this.dispatchBuyProduct(itemConfig).then(basketItem => {
                        this.$emit('afterAddToBasket', basketItem);

                        return basketItem;
                    });
                } else if (this.isSellOutOfStock(this.product, this.currentProductId, this.currentMerchantId)) {
                    this.setShowOutOfStockModal(true);
                } else {
                    this.quotaExceededModal = true;
                }

                return Promise.resolve();
            }
        },
    },
};
</script>
